import type {
    CoreQuestionListResponse,
    CoreQuestionResource,
} from '@dmp/qqms/data-access';
import type { StateGetter, StateSetter } from '../../state-types';
import { createCoreQuestion } from './create-core-question';
import { getCoreQuestion } from './get-core-question';
import { getCoreQuestionActiveShortIdList } from './get-core-question-active-list-by-short-id';
import { getCoreQuestionByShortId } from './get-core-question-by-short-id';
import { getCoreQuestionConfig } from './get-core-question-config';
import { getCoreQuestionList } from './get-core-question-list';
import { getCoreQuestionSortedIdList } from './get-core-question-sorted-id-list';
import { setCoreQuestionsFromResource } from './set-core-questions-from-resource';
import { switchEditingCoreQuestion } from './switch-editing-core-question';
import { updateCoreQuestion } from './update-core-question';

export const coreQuestionsActions = (set: StateSetter, get: StateGetter) => ({
    /** Get the Core Question list */
    getCoreQuestionList: () => getCoreQuestionList(get()),

    /** Get the list of all core question ids, sorted by priority. */
    getCoreQuestionSortedIdList: () => getCoreQuestionSortedIdList(get()),

    /** Get the list of only active core question ids by `shortId`, and sorted by priority. */
    getCoreQuestionActiveShortIdList: () =>
        getCoreQuestionActiveShortIdList(get()),

    /** Get Core Question by core question `id` */
    getCoreQuestion: (coreQuestionId: string) =>
        getCoreQuestion(coreQuestionId)(get()),

    /** Get Core Question by `shortId` */
    getCoreQuestionByShortId: (shortId: string) =>
        getCoreQuestionByShortId(shortId)(get()),

    /**
     * Get Core Question config, prep data for Core Question patching.
     * Collect all data of a core question and its answers, then convert to core question config
     **/
    getCoreQuestionConfig: (coreQuestionId: string) =>
        getCoreQuestionConfig(coreQuestionId)(get()),

    /** Set/consume Core Questions state with data from the API response  */
    setCoreQuestionsFromResource: (
        coreQuestionsResource: CoreQuestionListResponse,
        activeCardId?: string | null
    ) =>
        set(
            setCoreQuestionsFromResource(coreQuestionsResource, activeCardId),
            true
        ),

    /** Update core question partially */
    updateCoreQuestion: (
        coreQuestionId: string,
        partialCoreQuestion: Partial<CoreQuestionResource>
    ) => set(updateCoreQuestion(coreQuestionId, partialCoreQuestion), true),

    /**
     * Create a new Core Question to the state.
     * - Note: In the app, edit/create a core question equals to edit/create a linear question.
     * */
    createCoreQuestion: () => createCoreQuestion(get()),

    /**
     * Switching editing core question for the editing form
     * - Copy and convert the core question config to card and answer atoms.
     * - Passing THE SAME ID will reset the current question's form to its original config.
     */
    switchEditingCoreQuestion: (nextId: string) =>
        set(switchEditingCoreQuestion(nextId), true),
});
